/**
 * Apollo Client Provider
 * Configures GraphQL client with authentication
 */

import { FC, ReactNode, useMemo } from 'react';
import {
  ApolloClient,
  ApolloProvider as BaseApolloProvider,
  InMemoryCache,
  createHttpLink,
  ApolloLink,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { useRecoilValue } from 'recoil';
import { tokenState, guardState } from '@auth/authState';
import { apiConfig } from '@auth/authConfig';

interface ApolloProviderProps {
  children: ReactNode;
}

export const ApolloProvider: FC<ApolloProviderProps> = ({ children }) => {
  const token = useRecoilValue(tokenState);
  const guard = useRecoilValue(guardState);

  const client = useMemo(() => {
    // HTTP Link
    const httpLink = createHttpLink({
      uri: `${apiConfig.baseUrl}${apiConfig.graphqlEndpoint}`,
    });

    // Auth Link - adds Bearer token and x-guard header to requests
    const authLink = setContext((_, { headers }) => {
      const hasToken = token && token.length > 0;
      const hasGuard = guard.header && guard.header.length > 0;

      console.log('🔐 Apollo request:', {
        token: hasToken ? '✓ Present' : '✗ Missing',
        guard: hasGuard ? '✓ Present' : '✗ Missing',
      });

      return {
        headers: {
          ...headers,
          ...(hasToken ? { authorization: `Bearer ${token}` } : {}),
          ...(hasGuard ? { 'x-guard': guard.header } : {}),
        },
      };
    });

    // Error Link - handles GraphQL and network errors
    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) => {
          console.error(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
          );
        });
      }
      if (networkError) {
        console.error(`[Network error]: ${networkError}`);
      }
    });

    // Create Apollo Client
    return new ApolloClient({
      link: ApolloLink.from([errorLink, authLink, httpLink]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'network-only',
        },
        query: {
          fetchPolicy: 'network-only',
        },
      },
    });
  }, [token, guard]);

  return <BaseApolloProvider client={client}>{children}</BaseApolloProvider>;
};
